/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable no-script-url */

import React from 'react';
import PropTypes from 'prop-types';
import { get, capitalize } from 'lodash';
import moment from 'moment';
import { connect } from 'react-redux';
import { PageHeading } from 'web-ui-components/lib/typography/headings';
import { Alert } from 'web-ui-components/lib/molecules/nbas';
import { PaymentReview } from '../../../sharedComponents/PaymentReview/PaymentReview';
import RepeatingPaymentDescription from '../../../sharedComponents/RepeatingPaymentDescription/RepeatingPaymentDescription';
import paymentPropTypes from '../../../states/payments/propTypes';
import withContent from '../../../containers/withContent';
import confirmedContent, { contentPropTypes } from './confirmed.content';
import { dateFormat } from '../../../config/';
import {
	getConfirmedPaymentsList,
	getAwaitingAuthorisationPayments,
	getFailedPayments,
	isFutureDatedPayment,
	isExternalPayment,
	getPendingPayments,
} from '../../../states/payments/selectors';
import { isUserPaymentAuthoriser, getAuthorisationMessage } from '../../../helpers/fieldHelpers';

const mapState = (state) => ({
	confirmedPayments: getConfirmedPaymentsList(state),
	awaitingAuthorisationPayments: getAwaitingAuthorisationPayments(state),
	failedPayments: getFailedPayments(state),
	pendingPayments: getPendingPayments(state),
	isExternalPayment: (paymentId) => isExternalPayment(state, paymentId),
});

const ConfirmedPayments = ({
	failedPayments = [],
	confirmedPayments = [],
	awaitingAuthorisationPayments = [],
	pendingPayments = [],
	content,
	handleRoute,
	isExternalPayment,
	userId,
}) => {
	const getStatusTitle = (status, payment) => {
		const {
			authorisationRequired,
			repeatingTransactionScheduledHeader,
			fdpScheduledHeader,
			transferCompleteHeader,
			paymentSubmittedHeader,
		} = content;

		switch (status) {
			case 'warning':
				return authorisationRequired;
			case 'success':
				if (payment.type === 'repeating') return repeatingTransactionScheduledHeader;
				if (isFutureDatedPayment(payment)) {
					return fdpScheduledHeader;
				}
				if (isExternalPayment(payment.id)) {
					return paymentSubmittedHeader;
				}
				return transferCompleteHeader;
			case 'pending':
				return content.submissionPendingHeader;
			case 'failed':
				return content.submissionErrorHeader;
			default:
				return '';
		}
	};

	const getDate = ({ date }) => {
		if (moment.isMoment(date)) return date.format(dateFormat);
		return '';
	};

	const getStatusSubTitle = (
		status,
		{ type, date, confirmation: { authType = '', message = '', error = {} } = {}, authorisers },
		isFutureDated,
		userId
	) => {
		switch (status) {
			case 'warning':
				if (!isUserPaymentAuthoriser(authorisers, userId) || authorisers.length > 1) {
					return getAuthorisationMessage(authorisers, userId);
				}

				if (authType === 'HARD') return content.hardToken;
				if (authType === 'SOFT') return content.softToken;

				return content.thirdParty;
			case 'success':
				if (type === 'repeating') {
					return `${content.repeatingTransactionScheduledSubHeader} ${getDate({ date })}`;
				}
				if (isFutureDated) {
					return `${content.transactionScheduledSubHeader} ${getDate({ date })}`;
				}

				return message;
			case 'failed': {
				return get(error, 'message') || content.submissionErrorText;
			}
			case 'pending': {
				return get(error, 'message');
			}
			default:
				return '';
		}
	};

	const renderAuthoriseNavigation = () => (
		<Alert visible onClose={() => {}} type="tip">
			{content.authorisationNavigation}{' '}
			<a href="javascript:;" onClick={() => handleRoute('WEB-MANAGE-PAYMENT-STATUS')}>
				{content.authorisationNavigationButton}
			</a>
		</Alert>
	);

	const getRepeatingDescription = (payment) => {
		if (payment.type !== 'repeating') return null;
		return <RepeatingPaymentDescription payment={payment} />;
	};

	const renderPaymentsList = (payments, status, header, showHeader) => {
		const hardTokenAuthRequired = payments.some(
			(payment) =>
				payment.confirmation.authType === 'HARD' && isUserPaymentAuthoriser(get(payment, 'authorisers', []), userId)
		);

		const { fromLabel, toLabel, amtLabel, refLabel, dateLabel, typeLabel } = content;

		const paymentLabels = {
			fromLabel,
			toLabel,
			amtLabel,
			refLabel,
			dateLabel,
			typeLabel,
		};

		return (
			<div>
				{showHeader && <PageHeading data-test-id="confirmed-payments">{header}</PageHeading>}

				<div className="c-m-payment-review c-m-payment-review--header">
					<div className="c-m-payment-review__row c-m-payment-review__row--double">
						<span className="c-m-payment-review__label">{fromLabel}</span>
					</div>

					<div className="c-m-payment-review__row c-m-payment-review__row--double">
						<span className="c-m-payment-review__label">{toLabel}</span>
					</div>

					<div className="c-m-payment-review__row">
						<span className="c-m-payment-review__label">{amtLabel}</span>
					</div>

					<div className="c-m-payment-review__row c-m-payment-review__row--ref">
						<span className="c-m-payment-review__label">{refLabel}</span>
					</div>

					<div className="c-m-payment-review__row">
						<span className="c-m-payment-review__label">{dateLabel}</span>
					</div>

					<div className="c-m-payment-review__row">
						<span className="c-m-payment-review__label">{typeLabel}</span>
					</div>
				</div>
				{payments.map((payment) => (
					<PaymentReview
						key={payment.id}
						fromAccountName={get(payment, 'fromAccountDetails.displayName', '')}
						fromAccountHolder={get(payment, 'fromAccountDetails.metadata.account_name', '')}
						fromAccountSortCode={get(payment, 'fromAccountDetails.sortCode', '')}
						fromAccountNumber={get(payment, 'fromAccountDetails.accountNumber', '')}
						toAccountName={get(payment, 'toAccountDetails.displayName', '')}
						toAccountSortCode={get(payment, 'toAccountDetails.sortCode', '')}
						toAccountNumber={get(payment, 'toAccountDetails.accountNumber', '')}
						amount={payment.amount}
						reference={payment.reference}
						paymentDate={getDate(payment)}
						paymentType={payment.type === 'single' ? 'One-off' : capitalize(payment.type)}
						status={status}
						statusTitle={getStatusTitle(status, payment)}
						statusSubTitle={getStatusSubTitle(status, payment, isFutureDatedPayment(payment), userId)}
						labels={paymentLabels}
						repeating={getRepeatingDescription(payment)}
						hasStatus
					/>
				))}

				{hardTokenAuthRequired && renderAuthoriseNavigation()}
			</div>
		);
	};

	const showHeader =
		failedPayments.length + awaitingAuthorisationPayments.length + confirmedPayments.length + pendingPayments.length >
		1;

	return (
		<div>
			{!!failedPayments.length && renderPaymentsList(failedPayments, 'failed', content.FailedTableHeader, showHeader)}
			{!!pendingPayments.length &&
				renderPaymentsList(pendingPayments, 'pending', content.PendingTableHeader, showHeader)}
			{!!awaitingAuthorisationPayments.length &&
				renderPaymentsList(awaitingAuthorisationPayments, 'warning', content.AwaitingAuthTableHeader, showHeader)}
			{!!confirmedPayments.length &&
				renderPaymentsList(confirmedPayments, 'success', content.SubmittedTableHeader, showHeader)}
		</div>
	);
};

ConfirmedPayments.propTypes = {
	failedPayments: PropTypes.arrayOf(PropTypes.shape(paymentPropTypes)).isRequired,
	confirmedPayments: PropTypes.arrayOf(PropTypes.shape(paymentPropTypes)).isRequired,
	awaitingAuthorisationPayments: PropTypes.arrayOf(PropTypes.shape(paymentPropTypes)).isRequired,
	pendingPayments: PropTypes.arrayOf(PropTypes.shape(paymentPropTypes)).isRequired,
	content: PropTypes.shape(contentPropTypes),
	handleRoute: PropTypes.func.isRequired,
	userId: PropTypes.string.isRequired,
	isExternalPayment: PropTypes.func.isRequired,
};

const contentWrapper = withContent({ content: confirmedContent });
export default connect(mapState)(contentWrapper(ConfirmedPayments));
export const ConnectWrapper = connect(mapState)(ConfirmedPayments);
